/**
 * Broken Links Management Page
 *
 * Allows store owners to view and manage broken affiliate link reports.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useOutletContext, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  ExternalLink,
  Loader2,
  RefreshCw,
  Trash2,
  Edit3,
  Archive,
  Eye,
  Link as LinkIcon,
  Clock,
} from 'lucide-react';

interface Store {
  id: string;
  name: string;
  slug: string;
}

interface OutletContext {
  store: Store;
}

interface BrokenLinkReport {
  id: string;
  product_id: string;
  reported_url: string | null;
  status: 'pending' | 'reviewed' | 'fixed' | 'ignored' | 'invalid';
  notes: string | null;
  new_url: string | null;
  fixed_at: string | null;
  created_at: string;
  product_name: string;
  product_slug: string;
  product_image: string | null;
  current_affiliate_url: string | null;
}

interface StatusCounts {
  pending: number;
  reviewed: number;
  fixed: number;
  ignored: number;
  invalid: number;
}

const STATUS_COLORS: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
  pending: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-300',
    icon: <Clock className="w-4 h-4" />,
  },
  reviewed: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-300',
    icon: <Eye className="w-4 h-4" />,
  },
  fixed: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-700 dark:text-green-300',
    icon: <CheckCircle className="w-4 h-4" />,
  },
  ignored: {
    bg: 'bg-gray-100 dark:bg-gray-700',
    text: 'text-gray-600 dark:text-gray-400',
    icon: <XCircle className="w-4 h-4" />,
  },
  invalid: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-700 dark:text-red-300',
    icon: <AlertTriangle className="w-4 h-4" />,
  },
};

export default function BrokenLinksPage() {
  const { t } = useTranslation('store');
  const { storeId } = useParams<{ storeId: string }>();
  const context = useOutletContext<OutletContext | null>();
  const store = context?.store;

  const [reports, setReports] = useState<BrokenLinkReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [counts, setCounts] = useState<StatusCounts>({ pending: 0, reviewed: 0, fixed: 0, ignored: 0, invalid: 0 });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Edit modal state
  const [editingReport, setEditingReport] = useState<BrokenLinkReport | null>(null);
  const [newUrl, setNewUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const effectiveStoreId = store?.id || storeId || '';

  const loadReports = useCallback(async () => {
    if (!effectiveStoreId) return;

    try {
      setLoading(true);
      const params = new URLSearchParams({
        status: statusFilter,
        page: page.toString(),
        limit: '20',
      });

      const response = await fetch(
        `/api/ecommerce/stores/${effectiveStoreId}/broken-links?${params}`,
        { credentials: 'include' }
      );

      if (!response.ok) throw new Error('Failed to load reports');

      const data = await response.json() as {
        reports: BrokenLinkReport[];
        pagination: { pages: number };
        counts: StatusCounts;
      };

      setReports(data.reports);
      setTotalPages(data.pagination.pages);
      setCounts(data.counts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [effectiveStoreId, statusFilter, page]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleUpdateReport = async (reportId: string, updates: {
    status?: string;
    notes?: string;
    new_url?: string;
    update_product?: boolean;
    deactivate_product?: boolean;
  }) => {
    try {
      setSaving(true);
      const response = await fetch(
        `/api/ecommerce/stores/${effectiveStoreId}/broken-links`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ report_id: reportId, ...updates }),
        }
      );

      if (!response.ok) throw new Error('Failed to update report');

      setEditingReport(null);
      setNewUrl('');
      setNotes('');
      loadReports();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update report');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReport = async (reportId: string) => {
    if (!confirm('Delete this report?')) return;

    try {
      const response = await fetch(
        `/api/ecommerce/stores/${effectiveStoreId}/broken-links?report_id=${reportId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (!response.ok) throw new Error('Failed to delete report');
      loadReports();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete report');
    }
  };

  const openEditModal = (report: BrokenLinkReport) => {
    setEditingReport(report);
    setNewUrl(report.new_url || report.current_affiliate_url || '');
    setNotes(report.notes || '');
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading && reports.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
            {t('brokenLinks.title', 'Broken Link Reports')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {t('brokenLinks.description', 'Review and fix broken affiliate links reported by visitors')}
          </p>
        </div>
        <button
          onClick={() => loadReports()}
          disabled={loading}
          className="px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {t('common.refresh', 'Refresh')}
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Status Filter Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {(['pending', 'reviewed', 'fixed', 'ignored', 'invalid'] as const).map((status) => (
          <button
            key={status}
            onClick={() => {
              setStatusFilter(status);
              setPage(1);
            }}
            className={`px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 whitespace-nowrap transition-colors ${
              statusFilter === status
                ? `${STATUS_COLORS[status].bg} ${STATUS_COLORS[status].text}`
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {STATUS_COLORS[status].icon}
            {status.charAt(0).toUpperCase() + status.slice(1)}
            {counts[status] > 0 && (
              <span className="px-1.5 py-0.5 bg-white/50 dark:bg-black/20 rounded text-xs">
                {counts[status]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Reports List */}
      {reports.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {t('brokenLinks.noReports', 'No reports found')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            {statusFilter === 'pending'
              ? t('brokenLinks.noPending', 'Great! No broken links have been reported.')
              : t('brokenLinks.noStatus', `No ${statusFilter} reports.`)}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => (
            <div
              key={report.id}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4"
            >
              <div className="flex items-start gap-4">
                {/* Product Image */}
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                  {report.product_image ? (
                    <img
                      src={report.product_image}
                      alt={report.product_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <LinkIcon className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Report Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                        {report.product_name}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        Reported {formatDate(report.created_at)}
                      </p>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${STATUS_COLORS[report.status].bg} ${STATUS_COLORS[report.status].text}`}>
                      {STATUS_COLORS[report.status].icon}
                      {report.status}
                    </div>
                  </div>

                  {/* URL Info */}
                  <div className="mt-3 space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-500 dark:text-gray-400 w-24">Reported URL:</span>
                      <a
                        href={report.reported_url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:underline truncate flex items-center gap-1"
                      >
                        {report.reported_url?.substring(0, 60)}...
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    {report.new_url && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-500 dark:text-gray-400 w-24">New URL:</span>
                        <span className="text-green-600 dark:text-green-400 truncate">
                          {report.new_url.substring(0, 60)}...
                        </span>
                      </div>
                    )}
                    {report.notes && (
                      <div className="flex items-start gap-2 text-sm mt-2">
                        <span className="text-gray-500 dark:text-gray-400 w-24">Notes:</span>
                        <span className="text-gray-700 dark:text-gray-300">{report.notes}</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-4">
                    <Link
                      to={`/store/${effectiveStoreId}/products/${report.product_id}`}
                      className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center gap-1"
                    >
                      <Edit3 className="w-3 h-3" />
                      Edit Product
                    </Link>
                    <button
                      onClick={() => openEditModal(report)}
                      className="px-3 py-1.5 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors flex items-center gap-1"
                    >
                      <LinkIcon className="w-3 h-3" />
                      Fix Link
                    </button>
                    {report.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleUpdateReport(report.id, { status: 'ignored' })}
                          className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                          Ignore
                        </button>
                        <button
                          onClick={() => handleUpdateReport(report.id, { status: 'invalid' })}
                          className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        >
                          Invalid
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleDeleteReport(report.id)}
                      className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors ml-auto"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-200 dark:border-gray-700 rounded-lg disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-gray-600 dark:text-gray-400">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 border border-gray-200 dark:border-gray-700 rounded-lg disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Fix Link Modal */}
      {editingReport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <LinkIcon className="w-5 h-5 text-blue-600" />
                Fix Affiliate Link
              </h3>

              <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  Product: <span className="font-medium text-gray-900 dark:text-white">{editingReport.product_name}</span>
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 break-all">
                  Current: {editingReport.current_affiliate_url || 'None'}
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    New Affiliate URL
                  </label>
                  <input
                    type="url"
                    value={newUrl}
                    onChange={(e) => setNewUrl(e.target.value)}
                    placeholder="https://s.click.aliexpress.com/e/..."
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Notes (optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any notes about this fix..."
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setEditingReport(null)}
                  className="flex-1 py-2 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleUpdateReport(editingReport.id, {
                    status: 'fixed',
                    new_url: newUrl,
                    notes,
                    update_product: true,
                  })}
                  disabled={saving || !newUrl}
                  className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Fix & Update Product
                    </>
                  )}
                </button>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => handleUpdateReport(editingReport.id, {
                    status: 'fixed',
                    notes: 'Product deactivated - link permanently broken',
                    deactivate_product: true,
                  })}
                  disabled={saving}
                  className="w-full py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg flex items-center justify-center gap-2"
                >
                  <Archive className="w-4 h-4" />
                  Deactivate Product Instead
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
